import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApizWorkFlow, RunWorkflowResponse, RunWorkflowStatus } from '@compiere-ws/models/apiz-domains';
import { WFDataJSON } from '@compiere-ws/models/compiere-tree-json';
import { CompiereWFNextRequest } from '@compiere-ws/models/compiere-workflow-json';
import { SSEClient } from '@compiere-ws/utils/sse-client';
import { AppConfig } from '@iupics-config/app.config.service';
import { KeycloakService } from 'keycloak-angular';
import { Global } from '@iupics-manager/models/global-var';
import { catchError, filter, finalize, from, Observable, of, switchMap, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CompiereWorkflowService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(AppConfig);

  private url: string;
  private urlRunWF: string;
  private keycloak = inject(KeycloakService);

  constructor() {
    this.url = this.config.getBackendResourceWorkFlow('Doc_Status');
    this.urlRunWF = this.config.getBackendResourceWorkFlow('workflow');
  }

  getAvailaibleStatus(field_id): Observable<ApizWorkFlow> {
    const query = '?field_id=';
    return this.http.get<ApizWorkFlow>(`${this.url}${query}${field_id}`);
  }

  getNextStatus(status: CompiereWFNextRequest): Observable<any> {
    return this.http.post(this.url + '/next', status);
  }

  runWF(data: WFDataJSON): Observable<RunWorkflowResponse> {
    return this.http.post<RunWorkflowResponse>(`${this.urlRunWF}/run`, data).pipe(
      switchMap((response) => {
          if (response?.status === RunWorkflowStatus.COMPLETED || response?.status === RunWorkflowStatus.FAILED) {
          return of(response);
        }

        return from(this.keycloak.getToken()).pipe(
          switchMap((token) => this.streamWorkflowUpdates(response.workflowId, token))
        );
      })
    );
  }

  private streamWorkflowUpdates(workflowId: string, token: string): Observable<RunWorkflowResponse> {
    Global.activeXHRRequest++;

    const client = new SSEClient<RunWorkflowResponse>({
      url: `${this.urlRunWF}/${workflowId}/stream`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      pollingInterval: 2000,
      pollingUrl: `${this.urlRunWF}/${workflowId}/status`,
      shouldPoll: (): boolean => true,
      parseMessage: (message): RunWorkflowResponse => {
        if (
          message.event === 'workflow-completed' ||
          message.event === 'workflow-failed' ||
          message.event === 'workflow-cancelled' ||
          message.event === 'error'
        ) {
          return JSON.parse(message.data) as RunWorkflowResponse;
        }

        return null;
      },
      isTerminal: (data): boolean => {
        return data.status === RunWorkflowStatus.COMPLETED || data.status === RunWorkflowStatus.FAILED;
      },
      isError: (data): boolean => data.status === RunWorkflowStatus.FAILED,
      getErrorMessage: (data): string => data.message || 'Workflow failed',
    });

    return client.connect().pipe(
      filter((response) => response?.status === 'COMPLETED'),
      catchError((err) => throwError(() => err)),
      finalize(() => {
        if (Global.activeXHRRequest > 0) {
          Global.activeXHRRequest--;
        }
      })
    );
  }
}
